import React from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css, withTheme } from '@emotion/react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { Grid } from '@mui/material';
import { ComponentContainer } from '@styles/layout';
import { mq } from '@styles/breakpoints';
import { AppVariant } from '@model/config/brands';
import { Page } from '@model/common';
import { OpeningTimesTable } from '@components/common/opening-times/opening-times-table';
import { openingTimesDays, openingTimesHours } from '@model/common/opening-times';
import { Type } from '@components/footer/type';

/* ***************** *
 *       Types       *
 * ***************** */

export interface ContactDetailsProps {
  theme?: Theme;
  testId?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  marginBottom: -theme.custom.spacing.xxLarge,
  [mq.medium]: {
    marginBottom: -theme.custom.spacing.xxxLarge
  }
}));

const ContactDetailsSection = styled.div(({ theme }: ThemeProps) => ({
  paddingBottom: theme.custom.spacing.xxLarge,
  borderBottom: `1px dashed ${theme.custom.colors.group10.base}`
}));

const SectionTitle = styled.h4(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any)
}));

const Image = styled.img(({ theme }: ThemeProps) => ({
  height: 34,
  marginRight: theme.custom.spacing.xSmall,
  marginBottom: 2,
  verticalAlign: 'middle',
  width: 'auto'
}));

const WhatsAppLink = styled.a(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.group4.base,
  padding: `${theme.custom.spacing.medium}px 0px`,
  textUnderlineOffset: '4px',
  [':visited']: {
    color: theme.custom.colors.group4.base,
    textDecoration: 'none'
  }
}));

const contactDetailsStyles = css({
  [mq.small]: {
    display: 'flex',
    justifyContent: 'flex-start'
  }
});

const ContactDetailsLink = styled(Link)({
  textUnderlineOffset: '4px'
});

/* ***************************** *
 *    ContactDetailsComponent    *
 * ***************************** */

export const ContactDetailsComponent = (props: ContactDetailsProps) => {
  const { theme } = props;
  const t: any = useI18NextContext();
  const isWhiteLabel = AppVariant.isWhitelabel();

  return (
    <Container data-testid={props.testId || TestId.contactDetails.main}>
      <ContactDetailsSection>
        <SectionTitle>{t('contact-details__title--new-bookings')}</SectionTitle>
        <ComponentContainer noPadding spacing={theme!.custom.gridSpacing.default}>
          <Grid item>
            <p>
              {t('contact-details__text--new-bookings')}
              <WhatsAppLink href={t('common__brand_whatsapp')} target="_blank" rel="noreferrer noopener">
                {t('contact-details__link--new-bookings')}
                <Image src={'/assets/images/contact/whatsapp.png'} />
              </WhatsAppLink>
            </p>
          </Grid>
        </ComponentContainer>
      </ContactDetailsSection>
      <ContactDetailsSection>
        <SectionTitle>{t('contact-details__title--existing-bookings')}</SectionTitle>
        <ComponentContainer noPadding spacing={theme!.custom.gridSpacing.default}>
          <Grid item>
            <p>
              {t('contact-details__text--existing-bookings')}
              <ContactDetailsLink href={Page.MY_BOOKING}>
                {t('contact-details__link--manage-my-booking')}
              </ContactDetailsLink>
              .
            </p>
            <p>
              {t('contact-details__text--new-bookings-questions')}
              <ContactDetailsLink href={Page.HELP_CENTRE}>
                {t('contact-details__link--new-bookings-questions')}
              </ContactDetailsLink>
              .
            </p>
            <p>
              {t('contact-details__text--new-bookings-contact')}
              <ContactDetailsLink href={Page.MY_BOOKING}>
                {t('contact-details__link--manage-my-booking')}
              </ContactDetailsLink>
              {t('contact-details__text--new-bookings-contact-extra')}
              <WhatsAppLink href={t('common__brand_whatsapp')} target="_blank" rel="noreferrer noopener">
                {t('contact-details__link--new-bookings-whatsapp')}
                <Image src={'/assets/images/contact/whatsapp.png'} />
              </WhatsAppLink>
            </p>
          </Grid>
        </ComponentContainer>
      </ContactDetailsSection>
      {!isWhiteLabel && (
        <>
          <ContactDetailsSection>
            <SectionTitle>{t('contact-details__title--opening-times')}</SectionTitle>
            <OpeningTimesTable
              days={openingTimesDays}
              hours={openingTimesHours}
              variant={Type.CHECKOUT}
              contentCss={contactDetailsStyles}
            />
          </ContactDetailsSection>
        </>
      )}
      <ContactDetailsSection>
        <SectionTitle>{t('contact-details__title--postal-address')}</SectionTitle>
        <ComponentContainer noPadding spacing={theme!.custom.gridSpacing.default}>
          <Grid item>
            <p>{t('contact-details__text--postal-address')}</p>
          </Grid>
        </ComponentContainer>
      </ContactDetailsSection>
    </Container>
  );
};

export const ContactDetails = withTheme(ContactDetailsComponent);
